import { Trans, useTranslation } from "react-i18next";
import { ClipboardList, Files } from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import { Button } from "components/common/Button";

interface SmartSurveyorDataProcessingIndicatorProps {
  onViewResearchReport?: () => void;
  onViewUploadedFiles?: () => void;
  uploadedFilesCount?: number;
}

export const SmartSurveyorDataProcessingIndicator = ({
  onViewResearchReport,
  onViewUploadedFiles,
  uploadedFilesCount = 0,
}: SmartSurveyorDataProcessingIndicatorProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const canShowUploadedFilesButton =
    !!onViewUploadedFiles && uploadedFilesCount > 0;

  return (
    <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="bg-white/70 backdrop-blur-xl shadow-2xl rounded-3xl px-10 py-12 flex flex-col items-center max-w-md text-center border border-white/40">
        <div className="relative mb-6">
          <div className="w-16 h-16 rounded-full border-4 border-blue-200"></div>
          <div className="w-16 h-16 rounded-full border-4 border-blue-600 border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">
          {t("smartSurveyor.dataProcessingIndicator.title")}
        </h2>
        <p className="text-gray-600 mb-6">
          <Trans
            i18nKey="smartSurveyor.dataProcessingIndicator.description"
            values={{ email: user?.email ?? "" }}
            components={{
              bold: <strong className="font-semibold text-gray-800" />,
            }}
          />
        </p>
        <div className="flex space-x-2">
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-bounce"></div>
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-bounce delay-150"></div>
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-bounce delay-300"></div>
        </div>
        {(onViewResearchReport || canShowUploadedFilesButton) && (
          <div className="mt-6 flex flex-col gap-2">
            {onViewResearchReport && (
              <Button
                variant="default"
                size="lg"
                className="gap-2"
                onClick={onViewResearchReport}
                startAdornment={<ClipboardList size={18} />}
              >
                {t("smartSurveyor.reviewPage.viewResearchReport")}
              </Button>
            )}
            {canShowUploadedFilesButton && (
              <Button
                variant="default"
                size="lg"
                className="gap-2"
                onClick={onViewUploadedFiles}
                startAdornment={<Files size={18} />}
              >
                {t("smartSurveyor.uploadedFiles.viewButton", {
                  count: uploadedFilesCount,
                })}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
